import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Margin } from '../Button';
import { BadgeValues, MultiEnum } from '../Question/MultiEnum';

import { ModalContainer } from '../ModalContainer/';
import { formatString, Option } from '../utils';

import * as api from '../../api';
import * as logger from '../../logger';

import defaultStyles from '../../styles/formSetStyles.module.css';
import questionStyle from '../Question/index.module.css';
import styles from './index.module.css';

interface Props {
  modalSelectOptions: Option<api.Previsit.FamilyRelationship>[];
  modalLabel: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  value: string;
  options: Option<string>[];
  otherOptions: Option<string>[];
}

export function FamilyMedicalHistory(props: Props) {
  const { updateFieldValue, saveFieldValue, modalLabel, modalSelectOptions } =
    props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedListValue, setSelectedListValue] = useState('');
  const [selectedModalValues, setSelectedModalValues] = useState<
    api.Previsit.FamilyRelationship[]
  >([]);
  const [familyMedicalHistory, setFamilyMedicalHistory] =
    useState<api.Previsit.FamilyMedicalHistory>({ conditions: [] });
  const [badgeValues, setBadgeValues] = useState<BadgeValues[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    function parseValue(value: string): api.Previsit.FamilyMedicalHistory {
      try {
        if (value) {
          return JSON.parse(value);
        }
        return { conditions: [] };
      } catch (e) {
        logger.error(e);
      }
      return { conditions: [] };
    }
    const parsedValue = parseValue(props.value);
    setFamilyMedicalHistory(parsedValue);
    setBadgeValues(
      parsedValue.conditions.reduce((acc: BadgeValues[], item) => {
        acc.push({
          value: t(item.condition),
          badges: item.relationship.map((relationship) =>
            t(api.Previsit.familyRelationshipsMap[relationship]),
          ),
        });
        return acc;
      }, []),
    );
  }, [props.value, t]);

  const onModalClose = () => {
    setIsModalOpen(false);
    setSelectedListValue('');
    setSelectedModalValues([]);
  };

  const onModalItemClick = (modalValue: api.Previsit.FamilyRelationship) => {
    if (selectedModalValues.includes(modalValue)) {
      return setSelectedModalValues(
        selectedModalValues.filter((value) => value !== modalValue),
      );
    }
    setSelectedModalValues([...selectedModalValues, modalValue]);
  };

  const onBadgeClick = (value: string) => {
    const familyCondition = familyMedicalHistory.conditions.find(
      (f) => f.condition === value,
    );
    if (familyCondition) {
      setSelectedModalValues(familyCondition.relationship);
      setSelectedListValue(value);
      setIsModalOpen(true);
    }
  };

  const onItemClick = (value: string) => {
    const exists = familyMedicalHistory.conditions.find(
      (familyCondition) => familyCondition.condition === value,
    );
    if (exists) {
      const updatedValues: api.Previsit.FamilyMedicalHistory = {
        conditions: familyMedicalHistory.conditions.reduce(
          (acc: api.Previsit.ConditionWithRelationship[], item) => {
            if (item.condition !== value) {
              acc.push(item);
            }
            return acc;
          },
          [],
        ),
      };
      updateFieldValue(JSON.stringify(updatedValues));
      saveFieldValue(JSON.stringify(updatedValues));
    } else {
      setSelectedListValue(value);
      setIsModalOpen(true);
    }
  };

  const saveSelection = () => {
    const clearedExisting = familyMedicalHistory.conditions.filter(
      (value) => value.condition !== selectedListValue,
    );
    const updatedValues: api.Previsit.FamilyMedicalHistory = {
      conditions: [
        ...clearedExisting,
        {
          condition: selectedListValue,
          conditionRaw: selectedListValue,
          relationship: selectedModalValues,
          relationshipRaw: selectedModalValues,
        },
      ],
    };
    updateFieldValue(JSON.stringify(updatedValues));
    saveFieldValue(JSON.stringify(updatedValues));
    setIsModalOpen(false);
    setSelectedListValue('');
    setSelectedModalValues([]);
  };

  if (isModalOpen) {
    const component = (
      <>
        <label
          className={[questionStyle.Label, defaultStyles.Label].join(', ')}
        >
          {formatString(
            `${t(modalLabel)} ${t(selectedListValue).toUpperCase()}?`,
          )}
        </label>
        <MultiEnum
          values={selectedModalValues}
          options={modalSelectOptions}
          onItemClick={onModalItemClick}
        />
        <Button
          margin={Margin.Enum}
          type="button"
          disabled={!selectedModalValues.length}
          children={t('Continue')}
          onClick={saveSelection}
        />
      </>
    );
    return (
      <ModalContainer
        contentClassName={styles.OverflowScroll}
        isOpen={isModalOpen}
        onModalClose={onModalClose}
        component={component}
      />
    );
  }

  return (
    <MultiEnum
      {...props}
      values={_.uniqBy(familyMedicalHistory.conditions, 'condition').map(
        (condition) => condition.condition,
      )}
      onItemClick={onItemClick}
      optionBadges={badgeValues}
      onBadgeClick={onBadgeClick}
      sortOptions={true}
    />
  );
}
